import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { User, Mail, Lock, Camera } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';

function Profile({ user: initialUser }) {
  const [user, setUser] = useState(initialUser);
  const [loading, setLoading] = useState({});
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Profile form state
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    picture: user?.picture || ''
  });

  // Email change state
  const [emailData, setEmailData] = useState({
    new_email: '',
    current_password: ''
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        picture: user.picture || ''
      });
      setPreviewUrl(user.picture);
    }
  }, [user]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please select a JPEG, PNG, GIF, or WebP image.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/profile/upload-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Profile picture updated!');

        // Update user data
        const updatedUser = { ...user, picture: data.picture_url };
        setUser(updatedUser);
        setProfileData({ ...profileData, picture: data.picture_url });
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload picture');
        setPreviewUrl(user.picture); // Reset preview
      }
    } catch (error) {
      toast.error('Failed to upload picture');
      setPreviewUrl(user.picture); // Reset preview
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading({ profile: true });

    try {
      const response = await authFetch(`${API}/profile/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });

      if (response.ok) {
        toast.success('Profile updated successfully!');
        // Update local user data
        const updatedUser = { ...user, ...profileData };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading({ profile: false });
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setLoading({ email: true });

    try {
      const response = await authFetch(`${API}/profile/change-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData)
      });

      if (response.ok) {
        toast.success('Verification email sent! Check your new email address.');
        setEmailData({ new_email: '', current_password: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change email');
      }
    } catch (error) {
      toast.error('Failed to change email');
    } finally {
      setLoading({ email: false });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading({ password: true });

    try {
      const response = await authFetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (response.ok) {
        toast.success('Password changed successfully!');
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setLoading({ password: false });
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT">Profile Settings</h1>
          <p className="text-foreground-muted mt-2">Manage your account settings and preferences</p>
        </div>

        <Card className="p-6">
          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="email">Email</TabsTrigger>
              <TabsTrigger value="password">Password</TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <div className="space-y-6">
                <div className="flex items-center gap-6 pb-6 border-b">
                  <div className="relative">
                    {uploadingPicture ? (
                      <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                    ) : previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={profileData.name}
                        className="w-24 h-24 rounded-full object-cover border-2 border-slate-200"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    {!previewUrl && !uploadingPicture && (
                      <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-12 h-12 text-primary" />
                      </div>
                    )}
                    <input
                      type="file"
                      id="picture-upload"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={uploadingPicture}
                    />
                    <label
                      htmlFor="picture-upload"
                      className={`absolute bottom-0 right-0 bg-primary hover:bg-primary-hover text-white p-2 rounded-full shadow-lg transition-all cursor-pointer ${
                        uploadingPicture ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <Camera className="w-4 h-4" />
                    </label>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground-DEFAULT">{user?.name}</h3>
                    <p className="text-foreground-muted">{user?.email}</p>
                    <p className="text-sm text-primary mt-1 capitalize">{user?.role}</p>
                    <p className="text-xs text-foreground-muted mt-2">Click camera icon to upload new picture</p>
                  </div>
                </div>

                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      required
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                      Profile Picture URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={profileData.picture}
                      onChange={(e) => setProfileData({ ...profileData, picture: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                      placeholder="Or enter an image URL directly"
                    />
                    <p className="text-xs text-foreground-muted mt-1">
                      You can upload an image using the camera icon above or enter a URL here
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading.profile}
                    className="w-full bg-primary hover:bg-primary-hover text-white font-medium py-3 px-6 rounded-lg transition-all disabled:opacity-50"
                  >
                    {loading.profile ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </div>
            </TabsContent>

            {/* Email Tab */}
            <TabsContent value="email">
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Current Email</p>
                      <p className="text-sm text-blue-700 mt-1">{user?.email}</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleEmailChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                      New Email Address
                    </label>
                    <input
                      type="email"
                      required
                      value={emailData.new_email}
                      onChange={(e) => setEmailData({ ...emailData, new_email: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                      placeholder="newemail@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      required
                      value={emailData.current_password}
                      onChange={(e) => setEmailData({ ...emailData, current_password: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                      placeholder="Enter your current password"
                    />
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> A verification email will be sent to your new email address.
                      You must click the link in that email to complete the change.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading.email}
                    className="w-full bg-primary hover:bg-primary-hover text-white font-medium py-3 px-6 rounded-lg transition-all disabled:opacity-50"
                  >
                    {loading.email ? 'Sending...' : 'Change Email'}
                  </button>
                </form>
              </div>
            </TabsContent>

            {/* Password Tab */}
            <TabsContent value="password">
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Password Requirements</p>
                      <p className="text-sm text-blue-700 mt-1">Minimum 6 characters</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      required
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                      placeholder="Enter current password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                      placeholder="Enter new password (min 6 characters)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground-DEFAULT mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-primary/20 focus:ring-2 focus:ring-primary/10 transition-all rounded-lg"
                      placeholder="Confirm new password"
                    />
                  </div>

                  {passwordData.new_password && passwordData.confirm_password &&
                   passwordData.new_password !== passwordData.confirm_password && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                      Passwords do not match
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading.password}
                    className="w-full bg-primary hover:bg-primary-hover text-white font-medium py-3 px-6 rounded-lg transition-all disabled:opacity-50"
                  >
                    {loading.password ? 'Changing Password...' : 'Change Password'}
                  </button>
                </form>
              </div>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </Layout>
  );
}

export default Profile;
